/**
 * @fileoverview Module for `src/campaigns/lib/selectors.js`.
 */

import { isCampaignCompleted } from './accessors';
import { stateNamesByState } from './constants';

/**
 * Counts campaigns in a collection.
 *
 * @param {Object[]} campaigns - Campaign list.
 * @returns {number} Campaign count.
 */
function countCampaigns(campaigns) {
  return campaigns.length;
}

/**
 * Selects campaigns still available for adoption.
 *
 * @param {Object[]} campaigns - Campaign list.
 * @returns {Object[]} Available campaigns.
 */
function selectAvailableCampaigns(campaigns) {
  return campaigns.filter((campaign) => !isCampaignCompleted(campaign));
}

/**
 * Selects completed campaigns.
 *
 * @param {Object[]} campaigns - Campaign list.
 * @returns {Object[]} Completed campaigns.
 */
function selectCompletedCampaigns(campaigns) {
  return campaigns.filter((campaign) => isCampaignCompleted(campaign));
}

/**
 * Returns distinct state options present in a campaign collection.
 *
 * @param {Object[]} campaigns - Campaign list.
 * @returns {Array<[string, string]>} State abbreviation/name tuples.
 */
function selectCampaignStates(campaigns) {
  const allMappedStates = campaigns.map(({ state }) => state.toUpperCase());
  const uniqueMappedStates = new Set(allMappedStates);
  return [...uniqueMappedStates].map((state) => [state, stateNamesByState[state]]);
}

/**
 * Selects political campaigns.
 *
 * @param {Object[]} campaigns - Campaign list.
 * @returns {Object[]} Political campaigns.
 */
function selectPoliticalCampaigns(campaigns) {
  return campaigns.filter((campaign) => campaign.isPolitical || campaign.taxType === 'political');
}

/**
 * Selects social campaigns.
 *
 * @param {Object[]} campaigns - Campaign list.
 * @returns {Object[]} Social campaigns.
 */
function selectSocialCampaigns(campaigns) {
  return campaigns.filter((campaign) => campaign.isSocial || campaign.taxType === 'social');
}

export {
  countCampaigns,
  selectAvailableCampaigns,
  selectCampaignStates,
  selectCompletedCampaigns,
  selectPoliticalCampaigns,
  selectSocialCampaigns,
};
